// File: frontend/src/components/layout/DashboardHeader.js

import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useLanguage } from '../../contexts/LanguageContext';
import SignOutModal from '../auth/SignOutModal';
import styles from './DashboardHeader.module.css';
import { ASSETS_BASE_URL } from '../../config/constants';

const DashboardHeader = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [showSignOutModal, setShowSignOutModal] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  // Close mobile menu when modal opens
  useEffect(() => {
    if (showSignOutModal) {
      setMobileMenuOpen(false);
    }
  }, [showSignOutModal]);

  const handleSignOut = () => {
    setShowSignOutModal(true);
    setMobileMenuOpen(false);
  };

  return (
    <>
      <header className={styles.header}>
        <div className={styles.container}>
          <Link to="/" className={styles.logo} onClick={() => setMobileMenuOpen(false)}>
            <img 
              src={`${ASSETS_BASE_URL}/logos/allegory-logo-blue.svg`}
              alt={t('dashboard_header.logo_alt', 'Allegory Insurance')}
              className={styles.logoImage}
            />
          </Link>

          <nav className={`${styles.nav} ${mobileMenuOpen ? styles.navOpen : ''}`}>
            <Link 
              to="/dashboard" 
              className={styles.navLink}
              onClick={() => setMobileMenuOpen(false)}
            >
              {t('dashboard_header.nav.dashboard', 'Dashboard')}
            </Link>
            <Link 
              to="/account" 
              className={styles.navLink}
              onClick={() => setMobileMenuOpen(false)}
            >
              {t('dashboard_header.nav.account', 'Account')}
            </Link>

            <div className={styles.mobileUserSection}>
              <div className={styles.mobileUserInfo}>
                <span className={styles.mobileUserName}>
                  {user?.first_name} {user?.last_name}
                </span>
                <span className={styles.mobileUserEmail}>
                  {user?.email_address}
                </span>
              </div>
              <button
                onClick={handleSignOut}
                className={styles.mobileSignOutButton}
              >
                {t('dashboard_header.sign_out', 'Sign Out')}
              </button>
            </div>
          </nav>

          <div className={styles.userMenu}>
            <div className={styles.userInfo}>
              <span className={styles.userName}>
                {user?.first_name || t('dashboard_header.user_fallback', 'User')}
              </span>
            </div>
            <button
              onClick={() => setShowSignOutModal(true)}
              className={styles.signOutButton}
            >
              {t('dashboard_header.sign_out', 'Sign Out')}
            </button>
          </div>

          <button
            className={styles.mobileMenuButton}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={t('dashboard_header.toggle_menu_aria', 'Toggle menu')}
            aria-expanded={mobileMenuOpen}
          >
            {mobileMenuOpen ? (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            ) : (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M3 12H21M3 6H21M3 18H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            )}
          </button>
        </div>
      </header>

      {/* Mobile menu backdrop */}
      {mobileMenuOpen && (
        <div 
          className={styles.mobileBackdrop} 
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      <SignOutModal
        isOpen={showSignOutModal}
        onClose={() => setShowSignOutModal(false)}
      />
    </>
  );
};

export default DashboardHeader;